import { ActionField, useFormContext } from '@oscar/form';
import { Form, Layout, Button } from '@oscar/theme';
import { isValidValue } from '@oscar/utils';
import { findIndex, set } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import List from './components/List';
import './style.scss';
import { FieldValue, FormListProps, FormState, OriginFormProps } from './types';

const RenderChildComponent = React.memo(({ component, ...props }: any) => {
  return component ? component(props) : <></>;
});

const FormList: <T extends Record<string, any>>(
  props: FormListProps<T> & OriginFormProps,
) => JSX.Element = ({
  name,
  keyField,
  headerComponent,
  addedComponent,
  detailComponent,
  listComponent = List,
  onItemRenderer,
  emptyList,
  fields,
  operation,
  defaultValueAdded,
  readOnly,
  autoResize,
  fixedWidth = '270px',
  childProps = {},
  requireFields,
  title,
  allwayShowTitle,
  limit,
  isAddedItemOnTop,
  createButtonComponent,
}) => {
  const [formContext] = useFormContext();
  const [formState, setFormState] = useState<FormState>('Inital');
  const [field, setField] = useState<FieldValue>();
  const [valueSearchRequest, setValueSearchRequest] = useState('');
  const [selectedItem, setSelectedItem] = useState();
  const [showThickness, setShowThickness] = useState(false);
  const [showMaterial, setShowMaterial] = useState('');
  const handleInsulation = (e: any) => {
    if (e === 'N/A') {
      setShowThickness(true);
    } else {
      setShowThickness(false);
    }
  };

  useEffect(() => {
    if (showThickness === true) {
      setShowMaterial('N/A');
    } else {
      setShowMaterial('');
    }
  }, [showThickness]);
  const createItemHandler = useCallback(() => {
    setFormState('Create');
    const position = fields?.length || 0;
    setField({
      name: position,
      fieldKey: position,
      key: position,
      temp: position,
    });
    operation?.add({ ...defaultValueAdded?.(), temp: position }, position);
  }, [fields]);

  const clickItemHandler = useCallback(
    (item, currentField?: FieldValue) => {
      if (item) {
        setField(currentField);
        setSelectedItem(item);
        setFormState('Detail');
      }
    },
    [fields],
  );

  const removeItemHandler = useCallback(
    (currentField?: FieldValue) => {
      if (currentField) {
        const index = fields?.map(item => item.key)?.indexOf(currentField.key);

        if (index !== undefined && index > -1) {
          operation?.remove(index);
          setField(undefined);
        }
      }
      setSelectedItem(undefined);
      setFormState('Detail');
    },
    [fields, operation],
  );

  const resetItemHandler = useCallback(() => {
    setValueSearchRequest('');
    setSelectedItem(undefined);
    setFormState('Detail');
  }, [fields, operation]);

  const setItemHandler = useCallback(
    item => {
      if (item) {
        const values = formContext.form?.getFieldValue(name) || [];

        const index = findIndex(
          values,
          (_item: any) => _item?.[keyField] === item?.[keyField],
        );
        setField(fields?.[index]);
        setSelectedItem(values[index] || item);
        setFormState('Detail');
      }
    },
    [fields],
  );

  const isValidFields = (value: any) => {
    if (!requireFields) {
      return true;
    }
    for (let i = 0; i < requireFields?.length; i++) {
      if (!isValidValue(value[requireFields[i]])) {
        return false;
      }
    }
    return true;
  };

  const addNewItemHandler = useCallback(
    item => {
      if (item && isValidFields(item)) {
        const position = fields?.length || 0;
        operation?.add(item);
        setField({ name: position, fieldKey: position, key: position });
        setSelectedItem(item);
      }
    },
    [fields],
  );

  const addItemHandler = useCallback(
    (addedField: FieldValue) => {
      const values = formContext.form?.getFieldValue(name) || [];
      let currentItem: any;
      if (isValidFields(values[addedField.fieldKey])) {
        formContext.form?.setFieldsValue(
          set(
            {},
            name,
            values?.map((item: any) => {
              if (item?.temp === addedField.key) {
                delete item?.temp;
                currentItem = item;
              }
              return item;
            }),
          ),
        );
        clickItemHandler(currentItem, addedField);
      }
    },
    [fields],
  );

  useEffect(() => {
    if (formState === 'Inital') {
      const values = formContext.form?.getFieldValue(name) || [];
      if (values.length > 0) {
        if (isAddedItemOnTop) {
          clickItemHandler(
            values[values.length - 1],
            fields?.[values.length - 1],
          );
        } else {
          clickItemHandler(values[0], fields?.[0]);
        }
      }
    }
  }, [fields, formState]);

  const listValues = useMemo(() => {
    return formContext.form?.getFieldValue(name) || [];
  }, [fields]);

  const items = useMemo(() => {
    return listValues?.filter?.((item: any) => item?.temp === undefined) || [];
  }, [listValues]);

  const getItems = useCallback(
    (actionProps: any) => {
      const listValue = actionProps?.field?.getFieldValue(name);
      const values =
        listValue?.filter?.((item: any) => item?.temp === undefined) || [];
      const nextData = Array.from(values).map((item: any, index) => ({
        ...item,
        _field: fields?.[index],
      }));
      if (isAddedItemOnTop) {
        return nextData?.reverse();
      }
      return nextData;
    },
    [fields],
  );

  const getFields = (currentFields?: FieldValue[]) => {
    if (isAddedItemOnTop && currentFields) {
      return Array.from(currentFields)?.reverse();
    }
    return currentFields;
  };

  return (
    <div className="form-list">
      <Layout style={{ backgroundColor: '#fff' }}>
        {headerComponent && (
          <Layout.Content>
            <div
              style={{
                backgroundColor: '#F9F9F9',
              }}
            >
              <RenderChildComponent
                component={headerComponent}
                setItem={setItemHandler}
                valueSearchRequest={valueSearchRequest}
                setValueSearchRequest={setValueSearchRequest}
                readOnly={readOnly}
              />
            </div>
          </Layout.Content>
        )}
        <Layout
          style={{
            backgroundColor: '#fff',
            fontSize: '14px',
            lineHeight: '18px',
            height: '100%',
          }}
        >
          {(addedComponent || detailComponent) && (
            <Layout.Content
              style={{ overflowY: 'hidden', height: 'auto', width: '100%' }}
            >
              <div style={{ backgroundColor: '#fff' }}>
                {formState === 'Create' ? (
                  <RenderChildComponent
                    component={addedComponent}
                    title={title}
                    formState={formState}
                    field={field}
                    addItem={addItemHandler}
                    removeItem={removeItemHandler}
                    limit={limit}
                    data={listValues}
                    onCreateItem={handleInsulation}
                    setDefault={showMaterial}
                    disableThickness={showThickness}
                    {...childProps}
                  />
                ) : (
                  <RenderChildComponent
                    component={detailComponent}
                    title={title}
                    formState={formState}
                    field={field}
                    item={selectedItem}
                    addItem={addNewItemHandler}
                    removeItem={removeItemHandler}
                    readOnly={readOnly}
                    limit={limit}
                    notAllowAdd={items?.length === limit}
                    resetItem={resetItemHandler}
                    data={listValues}
                    {...childProps}
                  />
                )}
              </div>
            </Layout.Content>
          )}
          <Layout.Sider
            width={
              autoResize && items.length == 0
                ? '0px'
                : !addedComponent && !detailComponent
                ? '100%'
                : fixedWidth
            }
          >
            <ActionField
              name="renderList"
              request={{
                name: 'renderList',
              }}
            >
              {(actionProps: any) => {
                return (
                  <RenderChildComponent
                    component={listComponent}
                    fields={getFields(fields)}
                    selectedField={field}
                    data={getItems(actionProps)}
                    title={!readOnly || allwayShowTitle ? title : null}
                    onClickItem={clickItemHandler}
                    onCreateItem={createItemHandler}
                    onItemRenderer={onItemRenderer}
                    removeItem={removeItemHandler}
                    emptyList={emptyList}
                    readOnly={readOnly}
                    formState={formState}
                    limit={limit}
                    keyField={keyField}
                    createButtonComponent={createButtonComponent}
                    {...childProps}
                  />
                );
              }}
            </ActionField>
          </Layout.Sider>
        </Layout>
      </Layout>
    </div>
  );
};

export default function <T>(props: FormListProps<T>): JSX.Element {
  const { name, initialValue } = props;
  return (
    <Form.List name={name} initialValue={initialValue}>
      {(fields: any, operation: any) => (
        <FormList {...props} fields={fields} operation={operation} />
      )}
    </Form.List>
  );
}
